import { IonButton, IonCard, IonCardContent } from "@ionic/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import InputForm from "../../components/common/input-form";
import useForm from "../../hooks/use-form";
import { required } from "../../utils/validators";
import { Cuts, CutTypes, NetType } from "./types";

const ClothsForm = ({ defaultValues = null, onCutChange, onSubmit }) => {
	const { t } = useTranslation();

	const {
		formState: { state },
		errors = {},
		valid,
		handleUpdates,
		handleUpdatesBatch,
		handleSubmit,
		validate,
	} = useForm(
		{
			netLength: required,
			topNetCount: required,
			bottomNetCount: required,
			netCountVt: required,
			threadDiameter: required,
			runnage: required,
			netType: required,
			u1: required,
			u2: required,
			cut: required,
		},
		{
			initialState: {
				sign: -1,
				u1: 0.5,
				u2: 0.87,
				...(defaultValues as any),
			},
		}
	);

	const handleInputChanges =
		(inputType: string, updateHandler: Function, filedName = null) =>
		(inputValue: string | boolean | number) => {
			let result: string | number | boolean = inputValue;

			if (inputType === "number" && typeof result === "string") {
				result = result.includes(".") ? parseFloat(result) : parseInt(result);
			}
			updateHandler(result);
		};

	const handleFormSubmit = (data) => {
		if (!validate().valid) {
			return;
		}
		onSubmit(data);
	};

	const submitButtonText = null;

	useEffect(() => {
		if (state.netType === NetType.SQUARE) {
			handleUpdatesBatch(["u1", "u2"])([0.702, 0.702]);
		} else {
			if (state.netType === NetType.DIAMOND) {
				handleUpdatesBatch(["u1", "u2"])([0.5, 0.87]);
			}
		}
	}, [state.netType]);

	return (
		<>
			<IonCard>
				<IonCardContent>
					<div className="formCont">
						<InputForm
							value={state.netLength}
							id={"netLength"}
							label={`${t("system.cloths.form.netLength")} (mm)`}
							type="number"
							error={errors["netLength"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("netLength")
							)}
						/>
						<InputForm
							value={state.topNetCount}
							id={"topNetCount"}
							label={t("system.cloths.form.topNetCount")}
							type="number"
							error={errors["topNetCount"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("topNetCount")
							)}
						/>
						<InputForm
							value={state.bottomNetCount}
							id={"bottomNetCount"}
							label={t("system.cloths.form.bottomNetCount")}
							type="number"
							error={errors["bottomNetCount"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("bottomNetCount")
							)}
						/>
						<InputForm
							value={state.netCountVt}
							id={"netCountVt"}
							label={t("system.cloths.form.netCountVt")}
							type="number"
							error={errors["netCountVt"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("netCountVt")
							)}
						/>
						<InputForm
							value={state.threadDiameter}
							id={"threadDiameter"}
							label={`${t("system.cloths.form.threadDiameter")} (mm)`}
							type="number"
							error={errors["threadDiameter"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("threadDiameter")
							)}
						/>
						<InputForm
							value={state.runnage}
							id={"runnage"}
							label={`${t("system.cloths.form.runnage")} (m/kg)`}
							type="number"
							error={errors["runnage"]}
							onValueChange={handleInputChanges(
								"number",
								handleUpdates("runnage")
							)}
						/>

						<InputForm
							value={state.netType}
							id={"netType"}
							label={t("system.cloths.form.netType.label")}
							options={[
								{
									key: NetType.DIAMOND,
									label: t("system.cloths.form.netType.diamond"),
								},
								{
									key: NetType.SQUARE,
									label: t("system.cloths.form.netType.square"),
								},
							]}
							type="select"
							error={errors["netType"]}
							onValueChange={handleUpdates("netType")}
						/>
						<InputForm
							value={state.u1}
							id={"u1"}
							label="U1"
							type="number"
							error={errors["u1"]}
							onValueChange={handleInputChanges("number", handleUpdates("u1"))}
						/>
						<InputForm
							value={state.u2}
							id={"u2"}
							label="U2"
							type="number"
							error={errors["u2"]}
							onValueChange={handleInputChanges("number", handleUpdates("u2"))}
						/>
						<InputForm
							value={state.cut}
							id="cut"
							label={t("system.cloths.form.cut.label")}
							options={[
								{
									key: Cuts.FIXED_LEFT,
									label: t("system.cloths.form.cut.fixedLeft"),
								},
								{
									key: Cuts.FIXED_RIGHT,
									label: t("system.cloths.form.cut.fixedRight"),
								},
								{
									key: Cuts.SYMMETRICAL,
									label: t("system.cloths.form.cut.symmetrical"),
								},
							]}
							type="select"
							error={errors["cut"]}
							onValueChange={(value) => {
								handleUpdates("cut")(value);
								onCutChange(value, state.bottomNetCount, state.topNetCount);
							}}
						/>
						{state.cut && state.cut !== Cuts.SYMMETRICAL && (
							<>
								<InputForm
									value={state.cutType}
									id="cutType"
									label={t("system.cloths.form.cut.fixed")}
									options={[
										{
											key: CutTypes.N,
											label: "N",
										},
										{
											key: CutTypes.NB,
											label: "NB",
										},
										{
											key: CutTypes.TB,
											label: "TB",
										},
										{
											key: CutTypes.AB,
											label: "AB",
										},
									]}
									type="select"
									onValueChange={handleUpdates("cutType")}
								/>
								{(state.cutType === CutTypes.NB ||
									state.cutType === CutTypes.TB) && (
									<>
										<InputForm
											value={state.firstParam}
											id="firstParam"
											label={state.cutType === CutTypes.NB ? "N" : "T"}
											type="number"
											onValueChange={handleInputChanges(
												"number",
												handleUpdates("firstParam")
											)}
										/>
										<InputForm
											value={state.secondParam}
											id="secondParam"
											label="B"
											type="number"
											onValueChange={handleInputChanges(
												"number",
												handleUpdates("secondParam")
											)}
										/>
									</>
								)}
							</>
						)}
						{!valid && (
							<p
								style={{ textAlign: "center", marginBottom: 10, color: "red" }}
							>
								{t("validation.error")}
							</p>
						)}
					</div>
					<div className="ion-text-center">
						<IonButton onClick={handleSubmit(handleFormSubmit)}>
							{/*
                                                    TODO: Choose better default text for submit button
                                                */}
							{submitButtonText || t("general.calculate")}
						</IonButton>
					</div>
				</IonCardContent>
			</IonCard>
		</>
	);
};

export default ClothsForm;
